/** Duas trocas de indice dentro desta janela = o usuario esta procurando, nao vendo. */
export const FAST_SWIPE_WINDOW_MS = 1200;

export interface IndexScrollVelocity {
	direction: Readonly<Ref<'forward' | 'backward'>>;
	isFastScrolling: Readonly<Ref<boolean>>;
	/** Reporta o indice atual; retorna true quando foi uma transicao real. */
	report: (index: number) => boolean;
}

/**
 * Direcao + velocidade de rolagem a partir de uma sequencia de indices —
 * usado tanto pelo carrossel Embla (evento `select`) quanto pela paginacao
 * do feed de shorts (indice reativo). Extraido de duas copias quase
 * identicas que ja tinham divergido: uma delas nunca semeava o timestamp
 * base, entao a primeira transicao real (se dentro de `FAST_SWIPE_WINDOW_MS`
 * do mount) era erroneamente marcada como scroll rapido.
 */
export function useIndexScrollVelocity(
	windowMs: number = FAST_SWIPE_WINDOW_MS
): IndexScrollVelocity {
	const direction = ref<'forward' | 'backward'>('forward');
	const isFastScrolling = ref(false);

	let lastIndex: number | null = null;
	let lastChangeAt: number | null = null;
	let calmTimer: ReturnType<typeof setTimeout> | null = null;

	function report(index: number): boolean {
		const now = import.meta.client ? performance.now() : 0;

		// Primeiro indice desta instancia: so semeia lastIndex. `lastChangeAt`
		// fica null ate a primeira troca REAL, entao essa primeira troca nunca
		// tem uma troca anterior para comparar e nunca conta como rapida — so a
		// partir da segunda troca ha uma janela real para medir contra.
		if (lastIndex === null) {
			lastIndex = index;
			return false;
		}

		if (index === lastIndex) return false;

		direction.value = index > lastIndex ? 'forward' : 'backward';
		isFastScrolling.value =
			lastChangeAt !== null && now - lastChangeAt < windowMs;
		lastIndex = index;
		lastChangeAt = now;

		if (calmTimer !== null) clearTimeout(calmTimer);
		calmTimer = setTimeout(() => {
			isFastScrolling.value = false;
		}, windowMs);

		return true;
	}

	onBeforeUnmount(() => {
		if (calmTimer !== null) clearTimeout(calmTimer);
		calmTimer = null;
	});

	return { direction, isFastScrolling, report };
}
